<script module>
	// TODO: See if I can remove all this hubbub using Svelte Attachments (https://svelte.dev/docs/svelte/@attach)
	import { blur, fly, fade, scale, slide, type TransitionConfig } from 'svelte/transition';

	type TransitionFn = typeof blur | typeof fly | typeof fade | typeof scale | typeof slide;

	type HTMLAttrs = HTMLAttributes<HTMLButtonElement> & HTMLAttributes<HTMLAnchorElement>;

	const TRANSITION_NAME_TO_FN = {
		blur,
		fly,
		fade,
		scale,
		slide
	} as const;
	type IButtonProps = {
		children: Snippet;
		circle?: boolean;
		link?: string;
		disabled?: boolean;
		noOpener?: boolean;
		color?: 'primary' | 'secondary' | 'tertiary' | 'gray' | 'transparent';
		class?: string;
		transition?: null | {
			fn: keyof typeof TRANSITION_NAME_TO_FN;
			params?: TransitionConfig;
		};
		onclick?: () => void;
	};
</script>

<script lang="ts">
	import type { Snippet } from 'svelte';
	import { noop } from '../utils/noop';
	import type { HTMLAttributes } from 'svelte/elements';

	let {
		children,
		circle = false,
		link = '',
		disabled = false,
		noOpener = false,
		color = 'gray',
		class: className = '',
		transition = null,
		onclick = noop,
		...rest
	}: IButtonProps & HTMLAttrs = $props();
</script>

{#if link}
	{#if transition}
		{@const tFunc = TRANSITION_NAME_TO_FN[transition.fn]}
		<a
			href={link}
			class="button {circle ? 'circular' : ''} {disabled ? 'disabled' : ''} {color} {className}"
			aria-disabled={disabled}
			{onclick}
			rel={noOpener ? 'noreferrer' : ''}
			target={noOpener ? '_self' : '_blank'}
			{...rest}
			transition:tFunc|global={{ ...transition.params }}
		>
			{@render children()}</a
		>
	{:else}
		<a
			href={link}
			class="button {circle ? 'circular' : ''} {disabled ? 'disabled' : ''} {color} {className}"
			aria-disabled={disabled}
			{onclick}
			rel={noOpener ? 'noreferrer' : ''}
			target={noOpener ? '_self' : '_blank'}
			{...rest}
		>
			{@render children()}</a
		>
	{/if}
{:else if transition}
	{@const tFunc = TRANSITION_NAME_TO_FN[transition.fn]}
	<button
		class="button {circle ? 'circular' : ''} {color} {className}"
		{onclick}
		{disabled}
		transition:tFunc|global={{ ...transition.params }}
		{...rest}
	>
		{@render children()}</button
	>
{:else}
	<button
		class="button {circle ? 'circular' : ''} {color} {className}"
		{onclick}
		{disabled}
		{...rest}
	>
		{@render children()}</button
	>
{/if}

<style lang="postcss">
	:where(.button) {
		@apply flex items-center gap-1 rounded-lg p-2 text-gray-500 no-underline transition-colors duration-100 hover:cursor-pointer hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-300;
	}

	:is(.circular) {
		@apply rounded-full;
	}

	.button:disabled,
	.disabled {
		@apply cursor-not-allowed opacity-50;
	}

	:is(.primary) {
		@apply bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700;
	}

	:is(.secondary) {
		@apply bg-teal-400 text-white hover:bg-teal-500 dark:bg-teal-500 dark:hover:bg-teal-600;
	}

	:is(.tertiary) {
		@apply bg-gray-300 text-gray-900 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-300;
	}

	:is(.gray) {
		@apply bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700;
	}

	:is(.transparent) {
		@apply bg-transparent text-gray-900 hover:bg-gray-200 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700;
	}
</style>
